import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/ApiService';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import PageShell from '../ui/PageShell';
import {
  getPassportFlags,
  isPassportEnabled,
  joinPassportCampaign,
  listPassportCampaigns,
  type PassportCampaignSummary,
  type PassportFeatureFlags,
} from '../../services/api/passport';
import { PASSPORT_DISABLED_DESCRIPTION, PASSPORT_DISABLED_TITLE, formatEncounterDate } from './passportCopy';

const PassportCampaignsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<PassportCampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<number | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/passport/campaigns' }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const f = await getPassportFlags();
      if (!cancelled) {
        setFlags(f);
        setFlagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPassportCampaigns();
      setCampaigns(data);
      void apiService.trackEvent('passport_campaign_viewed', { metadata: { view: 'list' } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated || flagsLoading) return;
    if (!isPassportEnabled(flags)) return;
    void load();
  }, [authLoading, isAuthenticated, flagsLoading, flags, load]);

  const handleJoin = async (campaign: PassportCampaignSummary) => {
    setJoiningId(campaign.id);
    try {
      await joinPassportCampaign({ campaign_id: campaign.id });
      showToast(`Joined ${campaign.title}.`, 'success');
      void apiService.trackEvent('passport_campaign_joined', { metadata: { campaign_id: campaign.id } });
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to join campaign', 'error');
    } finally {
      setJoiningId(null);
    }
  };

  if (authLoading || !isAuthenticated || flagsLoading) {
    return <PageLoader message="Loading…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isPassportEnabled(flags)) {
    return (
      <PageShell contentClassName="!py-6 sm:!py-8">
        <PageHeader eyebrow="Bilt Passport" title="Campaigns" backLink={{ label: 'Back to Passport', to: '/passport' }} />
        <EmptyState title={PASSPORT_DISABLED_TITLE} description={PASSPORT_DISABLED_DESCRIPTION} />
      </PageShell>
    );
  }

  const noCampaignModulesEnabled = !flags?.sponsor_campaigns_enabled;

  return (
    <PageShell contentClassName="!py-6 sm:!py-8">
      <PageHeader
        eyebrow="Bilt Passport"
        title="Campaigns"
        description="Organizer, shop, and club activations built from real-world encounters — not scavenger-hunt gimmicks."
        backLink={{ label: 'Back to Passport', to: '/passport' }}
      />

      {noCampaignModulesEnabled ? (
        <EmptyState
          title="No active campaigns"
          description="Event and shop activations will appear here once organizers publish them."
        />
      ) : loading ? (
        <p className="text-gray-400 text-sm" role="status">
          Loading campaigns…
        </p>
      ) : error ? (
        <EmptyState
          title="Couldn't load campaigns"
          description={error}
          action={
            <Button type="button" variant="primary" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      ) : campaigns.length === 0 ? (
        <EmptyState title="No active campaigns" description="Check back closer to your next event." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {campaigns.map((c) => (
            <div key={c.public_id} className="card p-5">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                {c.campaign_type.replace(/_/g, ' ')}
              </p>
              <h3 className="text-base font-semibold text-white mb-1">{c.title}</h3>
              {c.description ? <p className="text-sm text-gray-400 mb-3">{c.description}</p> : null}
              <p className="text-xs text-gray-500 mb-3">
                {c.starts_at ? `Starts ${formatEncounterDate(c.starts_at)}` : ''}
                {c.ends_at ? ` · Ends ${formatEncounterDate(c.ends_at)}` : ''}
              </p>
              {c.participation ? (
                <span className="badge bg-white/[0.06] text-gray-300 border border-white/[0.1] text-xs">
                  {c.participation.objects_visited_count}/{c.participation.required_objects_total} visited
                </span>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={joiningId === c.id}
                  onClick={() => void handleJoin(c)}
                >
                  Join campaign
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
};

export default PassportCampaignsPage;
